import { useQueryClient } from '@tanstack/react-query'
import { notification } from 'antd'
import { $api, invalidateTypedQueries } from '../typedClient'

/**
 * The stored CrossroadsMappings document behind a reference-data row. The grid
 * rows are a projection of it, so history needs the real document to describe
 * the newest revision accurately.
 */
export function useCrossroadsMapping(mappingId: string | null | undefined) {
  return $api.useQuery(
    'get',
    '/crossroads/mappings/{id}',
    { params: { path: { id: mappingId ?? '' } } },
    {
      enabled: !!mappingId,
      // The drawer is opened on demand and must show the current state, not
      // whatever the 5-minute global staleTime is still holding.
      staleTime: 0,
      refetchOnMount: 'always',
    },
  )
}

/** Document audit history for one CrossroadsMappings document. */
export function useCrossroadsMappingHistory(
  mappingId: string | null | undefined,
) {
  return $api.useQuery(
    'post',
    '/crossroads/mappings/history',
    { params: { query: { mapping_id: mappingId ?? '' } } },
    {
      enabled: !!mappingId,
      // The drawer is opened on demand and must show the current state, not
      // whatever the 5-minute global staleTime is still holding.
      staleTime: 0,
      refetchOnMount: 'always',
    },
  )
}

/**
 * Rolls a mapping back to the state captured by one audit entry. The backend
 * audits the pre-restore document first, so a restore is itself undoable.
 */
export function useRestoreCrossroadsMapping() {
  const queryClient = useQueryClient()

  return $api.useMutation('post', '/crossroads/mappings/restore', {
    onSuccess: (data) => {
      invalidateTypedQueries(
        queryClient,
        'post',
        '/crossroads/mappings/history',
      )
      invalidateTypedQueries(queryClient, 'get', '/crossroads/mappings/{id}')
      notification.success({
        message: 'Mapping Restored',
        description: `"${data.display_name}" has been restored to the selected version.`,
      })
    },
    onError: (error) => {
      notification.error({
        message: 'Failed to Restore Mapping',
        description:
          error.detail?.map((d) => d.msg).join(', ') ||
          'An error occurred while restoring the mapping.',
      })
    },
  })
}
